import type { QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import type { ClinicianWorklistResult, IncompleteDataDiary, WorklistConsent } from "../api/worklistApi";
import { worklistKeys } from "../api/worklistKeys";
import type { BuildWorklistItemsResult, WorklistItem } from "./buildWorklistItems";

export type WorklistItemToComplete = Pick<
    WorklistItem,
    "id" | "kind" | "diaryId" | "trackerId" | "detailsTarget" | "consentId"
>;

let dismissedItemIds = new Set<string>();
const listeners = new Set<() => void>();

function notifyDismissedWorklistItems() {
    listeners.forEach((listener) => listener());
}

export function getDismissedWorklistItemIds(): Set<string> {
    return dismissedItemIds;
}

export function dismissWorklistItemId(itemId: string | undefined) {
    if (!itemId || dismissedItemIds.has(itemId)) return;
    dismissedItemIds = new Set(dismissedItemIds);
    dismissedItemIds.add(itemId);
    notifyDismissedWorklistItems();
}

export function resetDismissedWorklistItemIds() {
    if (dismissedItemIds.size === 0) return;
    dismissedItemIds = new Set();
    notifyDismissedWorklistItems();
}

/** Put restored rows back on the open list after the success screen hid them. */
export function undismissWorklistItemIds(itemIds: Array<string | undefined>) {
    const toRestore = itemIds.filter((itemId): itemId is string => Boolean(itemId) && dismissedItemIds.has(itemId));
    if (toRestore.length === 0) return;
    dismissedItemIds = new Set(dismissedItemIds);
    for (const itemId of toRestore) {
        dismissedItemIds.delete(itemId);
    }
    notifyDismissedWorklistItems();
}

export function useDismissedWorklistItemIds(): Set<string> {
    return useSyncExternalStore(
        (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getDismissedWorklistItemIds,
        getDismissedWorklistItemIds
    );
}

export function applyDismissedWorklistItems(
    result: BuildWorklistItemsResult,
    dismissedIds: Set<string>
): BuildWorklistItemsResult {
    if (dismissedIds.size === 0) return result;

    const dismissed = result.items.filter((item) => dismissedIds.has(item.id));
    if (dismissed.length === 0) return result;

    return {
        items: result.items.filter((item) => !dismissedIds.has(item.id)),
        formTaskCount: Math.max(0, result.formTaskCount - dismissed.filter((item) => item.kind === "form").length),
        incompleteDataCount: Math.max(
            0,
            result.incompleteDataCount - dismissed.filter((item) => item.kind === "event_details").length
        ),
        consentTaskCount: Math.max(
            0,
            result.consentTaskCount - dismissed.filter((item) => item.kind === "consent").length
        ),
    };
}

function removeFormTaskFromResult(
    data: ClinicianWorklistResult | undefined,
    item: WorklistItemToComplete
): ClinicianWorklistResult | undefined {
    if (!data) return data;

    return {
        ...data,
        diaries: data.diaries.map((diary) => {
            if (diary.diary_id !== item.diaryId) return diary;
            return {
                ...diary,
                trackers_view: diary.trackers_view.map((tracker) => {
                    if (tracker.tracker_id !== item.trackerId) return tracker;
                    return {
                        ...tracker,
                        is_due: false,
                        due_entries: [],
                        next_entry: null,
                    };
                }),
            };
        }),
    };
}

export function removeWorklistItemFromQueryData(queryClient: QueryClient, item: WorklistItemToComplete) {
    if (item.kind === "event_details") {
        const eventId = item.detailsTarget?.eventId;
        queryClient.setQueriesData<IncompleteDataDiary[]>({ queryKey: worklistKeys.incompleteData() }, (data) =>
            data?.filter((diary) =>
                eventId ? diary.detailsTarget?.eventId !== eventId : diary.diary_id !== item.diaryId
            )
        );
        return;
    }

    if (item.kind === "consent") {
        queryClient.setQueriesData<WorklistConsent[]>({ queryKey: worklistKeys.consents() }, (data) =>
            data?.filter((consent) => (item.consentId ? consent.consent_id !== item.consentId : true))
        );
        return;
    }

    queryClient.setQueriesData<ClinicianWorklistResult>({ queryKey: worklistKeys.forms() }, (data) =>
        removeFormTaskFromResult(data, item)
    );
}

/** Hide the completed row immediately so table tabs and the nav badge drop together. */
export function completeWorklistItemOptimistically(queryClient: QueryClient, item: WorklistItemToComplete | undefined) {
    if (!item) return;
    dismissWorklistItemId(item.id);
    void queryClient.cancelQueries({ queryKey: worklistKeys.all });
    removeWorklistItemFromQueryData(queryClient, item);
}
